/* eslint-disable no-case-declarations */
import {jsPDF as JsPDF} from "jspdf";
import moment from "moment";
import {printPdf} from "../../../../helpers/printPdf";
import {getTaxIdLabel} from "../../../../../shared/lib/getTaxIdLabel";
import {formatStreetWithHouseNumber} from "../../../../../shared/lib/formatBookerAddress";

// DIN 5008 Layout-Konstanten
const PAGE_WIDTH = 210;
const PAGE_PADDING = 20; // mm - Standard DIN 5008 Seitenrand
const INNER_WIDTH = PAGE_WIDTH - 2 * PAGE_PADDING; // 170mm Textbreite
const COLOR_GREY_TEXT = "#666666";
const DIN_5008_ADDRESS_LEFT = 20; // mm - Linker Rand fuer Fensterkuvert

// Vertikale Positionen nach DIN 5008
const SENDER_LINE_Y = 57; // mm
const RECIPIENT_START_Y = 65.5; // mm
const DATE_Y = 97; // mm
const SUBJECT_Y = 107; // mm
const BODY_START_Y = 115; // mm
const PAGE_BOTTOM_MARGIN = 268; // mm - Seitenumbruch-Grenze (angepasst fuer Fusszeile)

// Fusszeilen-Konstanten (DIN 5008 Geschaeftsbrieffuss)
const FOOTER_Y = 275; // mm - Vertikale Position der Fusszeile
const FOOTER_LINE_Y = FOOTER_Y - 2; // mm - Trennlinie ueber der Fusszeile
const FOOTER_COL_WIDTH = 56; // mm - Spaltenbreite (INNER_WIDTH / 3 ≈ 56mm)
const FOOTER_LINE_HEIGHT = 3.5; // mm - Zeilenabstand in der Fusszeile
const FOOTER_FONT_SIZE = 7; // pt - Schriftgroesse
const FOOTER_TEXT_COLOR = "#888888"; // Grau

// Logo-Abmessungen (maximal)
const LOGO_MAX_WIDTH = 50; // mm
const LOGO_MAX_HEIGHT = 25; // mm
const LOGO_X = PAGE_PADDING; // Linksbuendig (wie Rechnung)
const LOGO_Y = 10; // mm - Abstand oben

/**
 * Entfernt Zeichen die jsPDF mit der Standard-Schrift (Helvetica) nicht rendern kann.
 * Betrifft vor allem Emojis und andere Nicht-BMP Unicode-Zeichen, die zu falsch
 * berechneten Zeilenbreiten und extremem Buchstabenabstand fuehren.
 *
 * Behaelt: ASCII druckbar (0x20-0x7E), Latin-1 Supplement (0xA0-0xFF, inkl. Umlaute),
 * Latin Extended A/B (0x100-0x24F), Latin Extended Additional (0x1E00-0x1EFF),
 * sowie Zeilenumbrueche (\n, \r).
 */
function stripNonPrintableForPdf(text: string): string {
    if (!text) return "";

    var result = "";

    for (var i = 0; i < text.length; i++) {
        var code = text.charCodeAt(i);

        // Zeilenumbrueche beibehalten
        if (code === 10 || code === 13) {
            result += text[i];
            continue;
        }

        // ASCII druckbar (Space bis Tilde)
        if (code >= 0x20 && code <= 0x7e) {
            result += text[i];
            continue;
        }

        // Latin-1 Supplement (aeoeuess, Akzente, etc.)
        if (code >= 0xa0 && code <= 0xff) {
            result += text[i];
            continue;
        }

        // Latin Extended A/B (osteuropaeische Zeichen, etc.)
        if (code >= 0x100 && code <= 0x24f) {
            result += text[i];
            continue;
        }

        // Latin Extended Additional
        if (code >= 0x1e00 && code <= 0x1eff) {
            result += text[i];
            continue;
        }

        // Alles andere wird uebersprungen (Emojis, CJK, Arabisch, Surrogate Pairs, etc.)
    }

    // Mehrfach aufeinanderfolgende Leerzeichen (die durch entfernte Emojis entstehen) bereinigen
    result = result.replace(/ {2,}/g, " ");

    return result;
}

/**
 * Bereinigt HTML-Tags aus dem Text und wandelt Zeilenumbruch-Tags in Newlines um.
 * - `<br>` und `<br/>` werden zu `\n`
 * - `<p>...</p>` wird zu Inhalt + `\n\n`
 * - Alle restlichen HTML-Tags werden entfernt
 */
function stripHtmlTags(html: string): string {
    if (!html) return "";

    let text = html;

    // <br> und <br/> Varianten zu Newline
    text = text.split(/<br\s*\/?>/gi).join("\n");

    // <p>...</p> zu Inhalt + doppelter Zeilenumbruch
    text = text.split(/<p[^>]*>/gi).join("");
    text = text.split(/<\/p>/gi).join("\n\n");

    // Alle restlichen HTML-Tags entfernen
    text = text.split(/<[^>]*>/g).join("");

    // HTML-Entities dekodieren
    text = text.split("&amp;").join("&");
    text = text.split("&lt;").join("<");
    text = text.split("&gt;").join(">");
    text = text.split("&quot;").join('"');
    text = text.split("&#39;").join("'");
    text = text.split("&nbsp;").join(" ");

    // Ueberfluessige Leerzeilen am Ende entfernen
    text = text.replace(/\n{3,}/g, "\n\n");
    text = text.trim();

    return text;
}

// --- HTML-zu-PDF Formatierungs-Pipeline ---

interface TextSegment {
    text: string;
    bold: boolean;
    italic: boolean;
    underline: boolean;
    highlight: boolean;
    fontSize: number;
}

interface TextBlock {
    segments: TextSegment[];
    marginBefore: number;
    marginAfter: number;
}

interface RenderedLineSegment {
    text: string;
    fontStyle: string; // "normal" | "bold" | "italic" | "bolditalic"
    fontSize: number;
    underline: boolean;
    highlight: boolean;
}

interface RenderedLine {
    segments: RenderedLineSegment[];
    lineHeight: number;
}

interface LayoutResult {
    lines: RenderedLine[];
    marginBefore: number;
    marginAfter: number;
}

/**
 * Bestimmt die Font-Groesse und Margins fuer Heading-Elemente.
 * Gibt null zurueck wenn der Tag-Name kein Heading ist.
 */
function getHeadingConfig(tagName: string): {fontSize: number; marginBefore: number; marginAfter: number} | null {
    switch (tagName) {
        case "H1":
            return {fontSize: 18, marginBefore: 3, marginAfter: 1};
        case "H2":
            return {fontSize: 16, marginBefore: 3, marginAfter: 1};
        case "H3":
            return {fontSize: 14, marginBefore: 3, marginAfter: 1};
        case "H4":
            return {fontSize: 12, marginBefore: 3, marginAfter: 1};
        case "H5":
            return {fontSize: 11, marginBefore: 3, marginAfter: 1};
        case "H6":
            return {fontSize: 10, marginBefore: 3, marginAfter: 1};
        default:
            return null;
    }
}

/**
 * Prueft ob ein DOM-Knoten ein Block-Element ist (erzeugt neuen TextBlock).
 */
function isBlockElement(tagName: string): boolean {
    return tagName === "P" || tagName === "DIV" || tagName === "H1" || tagName === "H2" || tagName === "H3" || tagName === "H4" || tagName === "H5" || tagName === "H6";
}

/**
 * Parsed HTML-String in strukturierte TextBlocks mit Formatierungsinformationen.
 * Unterstuetzte Tags: h1-h6 (Ueberschriften), p/div (Absaetze), b/strong (Fett),
 * i/em (Kursiv), u (Unterstrichen), mark (Hervorhebung/Highlight), br (Zeilenumbruch).
 *
 * @param html - Der zu parsende HTML-String
 * @param baseFontSize - Standard-Schriftgroesse fuer normalen Text (in pt)
 * @returns Array von TextBlocks mit Segmenten und Abstandsinformationen
 */
function parseHtmlToBlocks(html: string, baseFontSize: number): TextBlock[] {
    if (!html) return [];

    // Pre-Processing: Newlines in <br> umwandeln, damit Plain-Text-Zeilenumbrueche funktionieren
    var preprocessed = html.split("\n").join("<br>");

    var parser = new DOMParser();
    var doc = parser.parseFromString(preprocessed, "text/html");
    var body = doc.body;

    if (!body) return [];

    var blocks: TextBlock[] = [];
    var currentBlock: TextBlock = {segments: [], marginBefore: 0, marginAfter: 0};

    /**
     * Startet einen neuen TextBlock. Der aktuelle Block wird nur gespeichert
     * wenn er Segmente enthaelt.
     */
    function flushBlock(marginBefore: number, marginAfter: number): void {
        if (currentBlock.segments.length > 0) {
            blocks.push(currentBlock);
        } else if (blocks.length > 0) {
            // Leerer Block (z.B. durch aufeinanderfolgende <br>-Tags fuer Leerzeilen):
            // Bestehenden Abstand beibehalten und eine Zeilenhoehe hinzufuegen,
            // damit die Leerzeile im PDF sichtbar bleibt.
            marginBefore = currentBlock.marginBefore + baseFontSize * 0.4;
        }
        currentBlock = {segments: [], marginBefore: marginBefore, marginAfter: marginAfter};
    }

    /**
     * Rekursive DOM-Traversierung mit Style-Stack.
     * Inline-Elemente modifizieren den Stack, Block-Elemente erzeugen neue Blocks.
     */
    function walkNode(node: Node, bold: boolean, italic: boolean, underline: boolean, highlight: boolean, fontSize: number): void {
        if (node.nodeType === Node.TEXT_NODE) {
            var textContent = node.textContent || "";
            var sanitized = stripNonPrintableForPdf(textContent);
            if (sanitized.length > 0) {
                currentBlock.segments.push({
                    text: sanitized,
                    bold: bold,
                    italic: italic,
                    underline: underline,
                    highlight: highlight,
                    fontSize: fontSize,
                });
            }
            return;
        }

        if (node.nodeType !== Node.ELEMENT_NODE) return;

        var element = node as Element;
        var tagName = element.tagName;

        // <br> erzeugt einen neuen TextBlock (Zeilenumbruch)
        if (tagName === "BR") {
            flushBlock(0, 0);
            return;
        }

        // Block-Elemente starten einen neuen Block
        if (isBlockElement(tagName)) {
            var headingConfig = getHeadingConfig(tagName);
            if (headingConfig) {
                // Heading: neuer Block mit groesserer Schrift und eigenem Abstand
                flushBlock(headingConfig.marginBefore, headingConfig.marginAfter);

                // Kinder mit Heading-Styles traversieren (Headings sind immer bold)
                for (var i = 0; i < node.childNodes.length; i++) {
                    walkNode(node.childNodes[i], true, italic, underline, highlight, headingConfig.fontSize);
                }

                // Block nach dem Heading abschliessen, marginAfter setzen
                currentBlock.marginAfter = headingConfig.marginAfter;
                flushBlock(0, 0);
                return;
            }

            // Paragraph/Div: neuer Block mit Standard-Abstaenden
            flushBlock(2, 2);

            for (var j = 0; j < node.childNodes.length; j++) {
                walkNode(node.childNodes[j], bold, italic, underline, highlight, fontSize);
            }

            currentBlock.marginAfter = 2;
            flushBlock(0, 0);
            return;
        }

        // Inline-Elemente: Style-Stack modifizieren
        var newBold = bold;
        var newItalic = italic;
        var newUnderline = underline;
        var newHighlight = highlight;

        if (tagName === "B" || tagName === "STRONG") {
            newBold = true;
        } else if (tagName === "I" || tagName === "EM") {
            newItalic = true;
        } else if (tagName === "U") {
            newUnderline = true;
        } else if (tagName === "MARK") {
            newHighlight = true;
        }

        for (var k = 0; k < node.childNodes.length; k++) {
            walkNode(node.childNodes[k], newBold, newItalic, newUnderline, newHighlight, fontSize);
        }
    }

    // DOM-Baum traversieren
    for (var c = 0; c < body.childNodes.length; c++) {
        walkNode(body.childNodes[c], false, false, false, false, baseFontSize);
    }

    // Letzten Block flushen
    if (currentBlock.segments.length > 0) {
        blocks.push(currentBlock);
    }

    return blocks;
}

/**
 * Bestimmt den jsPDF-Fontestyle-String aus den Formatierungs-Flags.
 */
function getFontStyle(bold: boolean, italic: boolean): string {
    if (bold && italic) return "bolditalic";
    if (bold) return "bold";
    if (italic) return "italic";
    return "normal";
}

/**
 * Layout-Engine: Bricht TextBlocks in Zeilen um basierend auf der verfuegbaren Breite.
 * Jeder TextBlock wird in ein LayoutResult mit zeilenweise angeordneten Segmenten umgewandelt.
 *
 * @param doc - Das jsPDF-Dokument (fuer Textbreiten-Messung)
 * @param blocks - Geparste TextBlocks aus parseHtmlToBlocks
 * @param maxWidth - Maximale Zeilenbreite in mm
 * @returns Array von LayoutResults mit zeilenweise angeordneten Segmenten
 */
function layoutBlocksToLines(doc: JsPDF, blocks: TextBlock[], maxWidth: number): LayoutResult[] {
    var results: LayoutResult[] = [];

    for (var b = 0; b < blocks.length; b++) {
        var block = blocks[b];
        var lines: RenderedLine[] = [];
        var currentLineSegments: RenderedLineSegment[] = [];
        var currentLineWidth = 0;
        var currentLineMaxFontSize = 0;

        /**
         * Schliesst die aktuelle Zeile ab und startet eine neue.
         */
        function finishLine(): void {
            if (currentLineSegments.length > 0) {
                lines.push({
                    segments: currentLineSegments,
                    lineHeight: currentLineMaxFontSize * 0.4,
                });
            }
            currentLineSegments = [];
            currentLineWidth = 0;
            currentLineMaxFontSize = 0;
        }

        for (var s = 0; s < block.segments.length; s++) {
            var segment = block.segments[s];
            var fontStyle = getFontStyle(segment.bold, segment.italic);

            // jsPDF fuer korrekte Textbreiten-Messung konfigurieren
            doc.setFont("Helvetica", fontStyle);
            doc.setFontSize(segment.fontSize);

            // Text an Leerzeichen aufteilen, um Wort-fuer-Wort-Umbruch zu ermoeglichen
            var words = segment.text.split(" ");

            for (var w = 0; w < words.length; w++) {
                var word = words[w];
                if (word.length === 0 && w > 0) {
                    // Leeres Wort ueberspringen (von doppelten Leerzeichen), ausser am Anfang
                    continue;
                }

                // Leerzeichen vor dem Wort beruecksichtigen (ausser am Zeilenanfang)
                var prefix = (currentLineWidth > 0 && w > 0) || (currentLineWidth > 0 && w === 0 && s > 0) ? " " : "";
                var textToMeasure = prefix + word;
                var wordWidth = doc.getTextWidth(textToMeasure);

                // Pruefen ob das Wort noch in die aktuelle Zeile passt
                if (currentLineWidth > 0 && currentLineWidth + wordWidth > maxWidth) {
                    // Zeile voll: abschliessen und neue Zeile beginnen
                    finishLine();
                    // Wort ohne Prefix auf neue Zeile setzen
                    prefix = "";
                    textToMeasure = word;
                    wordWidth = doc.getTextWidth(textToMeasure);
                }

                // Wort zur aktuellen Zeile hinzufuegen
                currentLineSegments.push({
                    text: prefix + word,
                    fontStyle: fontStyle,
                    fontSize: segment.fontSize,
                    underline: segment.underline,
                    highlight: segment.highlight,
                });
                currentLineWidth += wordWidth;

                if (segment.fontSize > currentLineMaxFontSize) {
                    currentLineMaxFontSize = segment.fontSize;
                }
            }
        }

        // Letzte Zeile des Blocks abschliessen
        finishLine();

        results.push({
            lines: lines,
            marginBefore: block.marginBefore,
            marginAfter: block.marginAfter,
        });
    }

    return results;
}

/**
 * Rendert HTML-formatierten Text ins PDF-Dokument mit voller Formatierungsunterstuetzung.
 * Durchlaeuft die 3-Phasen-Pipeline: HTML-Parsing → Layout → PDF-Rendering.
 *
 * Unterstuetzte HTML-Tags:
 * - `<h1>` bis `<h6>`: Ueberschriften (fett, verschiedene Groessen)
 * - `<p>`, `<div>`: Absaetze mit Abstaenden
 * - `<b>`, `<strong>`: Fettschrift
 * - `<i>`, `<em>`: Kursivschrift
 * - `<u>`: Unterstreichung
 * - `<mark>`: Hervorhebung (gelber Hintergrund)
 * - `<br>`: Zeilenumbruch
 *
 * @param doc - Das jsPDF-Dokument
 * @param html - Der HTML-String der gerendert werden soll
 * @param startY - Start-Y-Position in mm
 * @param baseFontSize - Standard-Schriftgroesse in pt
 * @returns Die Y-Position nach dem gerenderten Text
 */
function renderHtmlTextBlock(doc: JsPDF, html: string, startY: number, baseFontSize: number): number {
    // Phase 1: HTML in strukturierte TextBlocks parsen
    var blocks = parseHtmlToBlocks(html, baseFontSize);

    if (blocks.length === 0) return startY;

    // Phase 2: TextBlocks in Zeilen umbrechen (Layout)
    var layoutResults = layoutBlocksToLines(doc, blocks, INNER_WIDTH);

    // Phase 3: Zeilen ins PDF rendern
    var currentY = startY;

    for (var r = 0; r < layoutResults.length; r++) {
        var result = layoutResults[r];

        // Abstand vor dem Block
        currentY += result.marginBefore;

        for (var l = 0; l < result.lines.length; l++) {
            var line = result.lines[l];

            // Seitenumbruch pruefen
            if (currentY > PAGE_BOTTOM_MARGIN) {
                doc.addPage();
                currentY = PAGE_PADDING;
            }

            var currentX = DIN_5008_ADDRESS_LEFT;

            for (var seg = 0; seg < line.segments.length; seg++) {
                var segment = line.segments[seg];

                // Font konfigurieren
                doc.setFont("Helvetica", segment.fontStyle);
                doc.setFontSize(segment.fontSize);

                var segmentWidth = doc.getTextWidth(segment.text);

                // Highlight-Hintergrund zeichnen (gelbes Rechteck hinter dem Text)
                if (segment.highlight) {
                    // Fuehrende Leerzeichen nicht markieren (analog zu underline)
                    var highlightTrimmedText = segment.text.replace(/^ +/, "");
                    var highlightLeadingSpaceWidth = segmentWidth - doc.getTextWidth(highlightTrimmedText);
                    var highlightWidth = segmentWidth - highlightLeadingSpaceWidth;
                    // Rechteck-Hoehe basierend auf Schriftgroesse, Y-Offset leicht ueber der Basislinie
                    var highlightHeight = segment.fontSize * 0.4;
                    var highlightY = currentY - highlightHeight + 0.5;

                    doc.setFillColor("#FFFF00"); // Standard-<mark>-Farbe (gelb)
                    doc.rect(currentX + highlightLeadingSpaceWidth, highlightY, highlightWidth, highlightHeight, "F");
                }

                // Textfarbe sicherstellen (schwarz), da setFillColor vom Highlight die Textfarbe beeinflusst
                doc.setTextColor(0, 0, 0);

                // Text rendern
                doc.text(segment.text, currentX, currentY);

                // Unterstreichung zeichnen
                if (segment.underline) {
                    var underlineY = currentY + 0.5; // leicht unter der Textbasislinie
                    // Text-Offset: fuehrende Leerzeichen nicht unterstreichen
                    var trimmedText = segment.text.replace(/^ +/, "");
                    var leadingSpaceWidth = doc.getTextWidth(segment.text) - doc.getTextWidth(trimmedText);

                    doc.setDrawColor(0, 0, 0);
                    doc.setLineWidth(0.2);
                    doc.line(currentX + leadingSpaceWidth, underlineY, currentX + segmentWidth, underlineY);
                }

                currentX += segmentWidth;
            }

            currentY += line.lineHeight;
        }

        // Abstand nach dem Block
        currentY += result.marginAfter;
    }

    return currentY;
}

/**
 * Input-Daten fuer die Brief-PDF-Generierung
 */
export interface LetterPdfCourseDetailsData {
    courseName?: string;
    courseNumber?: string;
    locationName?: string;
    locationStreet?: string;
    locationCity?: string;
    courseStartDate?: string;
    courseEndDate?: string;
    courseTime?: string;
    coursePrice?: string;
    courseLessonsCount?: string;
    attendeeName?: string;
}

export interface LetterPdfData {
    senderName?: string;
    senderStreet?: string;
    senderStreetNumber?: string;
    senderZip?: string;
    senderCity?: string;
    senderLogoBase64?: string;
    senderPhone?: string;
    senderEmail?: string;
    senderWebsite?: string;
    senderBankName?: string;
    senderIban?: string;
    senderBic?: string;
    senderTaxId?: string;
    senderTaxNumber?: string;
    /**
     * Land des Absenders (CompanySetting.ownerCountry) — Fallback für das
     * Steuer-ID-Label, wenn die ID kein CHE-/ATU-Präfix trägt (#514).
     */
    senderCountry?: string;
    recipientFirstName?: string;
    recipientLastName?: string;
    recipientStreet?: string;
    recipientHouseNumber?: string;
    recipientPostCode?: string;
    recipientCity?: string;
    recipientAddition?: string;
    subject: string;
    headerText: string;
    footerText: string;
    signature: string;
    date?: string;
    courseDetails?: LetterPdfCourseDetailsData;
}

export type LetterPdfUseCase = "preview" | "export" | "print";

/**
 * Rendert einen Textblock mit automatischem Zeilenumbruch und Seitenumbruch.
 * Gibt die aktuelle Y-Position nach dem Rendern zurueck.
 */
function renderTextBlock(doc: JsPDF, text: string, startY: number, fontSize: number): number {
    doc.setFontSize(fontSize);
    doc.setFont("Helvetica", "normal");

    // Nicht darstellbare Zeichen (Emojis etc.) entfernen, bevor jsPDF die Zeilenbreite berechnet
    const sanitizedText = stripNonPrintableForPdf(text);
    const lines = doc.splitTextToSize(sanitizedText, INNER_WIDTH) as string[];
    const lineHeight = fontSize * 0.4; // Angemessene Zeilenhoehe in mm
    let currentY = startY;

    for (let i = 0; i < lines.length; i++) {
        // Seitenumbruch wenn noetig
        if (currentY > PAGE_BOTTOM_MARGIN) {
            doc.addPage();
            currentY = PAGE_PADDING;
        }

        doc.text(lines[i], DIN_5008_ADDRESS_LEFT, currentY);
        currentY += lineHeight;
    }

    return currentY;
}

/**
 * Ermittelt die natuerlichen Abmessungen eines Base64-codierten Bildes.
 * Verwendet ein temporaeres Image-Objekt im Browser.
 *
 * @param base64 - Base64-String des Bildes (mit oder ohne Data-URI-Prefix)
 * @returns Promise mit Breite und Hoehe in Pixeln
 */
function getImageDimensions(base64: string): Promise<{width: number; height: number}> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({width: img.naturalWidth, height: img.naturalHeight});
        img.onerror = reject;
        // Data-URI-Prefix sicherstellen
        const src = base64.indexOf("data:") === 0 ? base64 : "data:image/png;base64," + base64;
        img.src = src;
    });
}

// Kursdetails-Rendering-Konstanten
const COURSE_DETAILS_LABEL_X = DIN_5008_ADDRESS_LEFT;
const COURSE_DETAILS_VALUE_X = DIN_5008_ADDRESS_LEFT + 35; // 35mm Einrueckung fuer Werte
const COURSE_DETAILS_LINE_HEIGHT = 4.5; // mm pro Zeile
const COLOR_COURSE_DETAILS_LINE = "#cccccc";

/**
 * Rendert einen Kursdetails-Block als strukturierte Key-Value-Liste im PDF.
 * Wird zwischen Betreff und Briefkoerper platziert.
 *
 * @param doc - Das jsPDF-Dokument
 * @param details - Die Kursdetails-Daten
 * @param startY - Y-Position fuer den Start des Blocks
 * @returns Die Y-Position nach dem gerenderten Block
 */
function renderCourseDetailsBlock(doc: JsPDF, details: LetterPdfCourseDetailsData, startY: number): number {
    // Key-Value-Paare sammeln (nur Zeilen mit Daten rendern)
    const rows: Array<{label: string; value: string}> = [];

    if (details.courseName) {
        rows.push({label: "Kurs:", value: details.courseName});
    }

    if (details.attendeeName) {
        rows.push({label: "Teilnehmer:", value: details.attendeeName});
    }

    if (details.locationName) {
        // Ort als mehrzeiligen Eintrag aufbauen
        let locationValue = details.locationName;

        if (details.locationStreet) {
            locationValue += "\n" + details.locationStreet;
        }

        if (details.locationCity) {
            locationValue += (details.locationStreet ? ", " : "\n") + details.locationCity;
        }

        rows.push({label: "Ort:", value: locationValue});
    }

    // Zeitraum: Start- und Enddatum kombinieren
    if (details.courseStartDate) {
        const zeitraum = details.courseEndDate && details.courseEndDate !== details.courseStartDate ? `${details.courseStartDate} - ${details.courseEndDate}` : details.courseStartDate;

        rows.push({label: "Zeitraum:", value: zeitraum});
    }

    if (details.courseTime) {
        rows.push({label: "Uhrzeit:", value: details.courseTime});
    }

    if (details.courseLessonsCount) {
        rows.push({label: "Einheiten:", value: details.courseLessonsCount});
    }

    if (details.coursePrice) {
        rows.push({label: "Preis:", value: details.coursePrice});
    }

    // Wenn keine Zeilen vorhanden, nichts rendern
    if (rows.length === 0) {
        return startY;
    }

    let currentY = startY;

    // Ueberschrift "Kursdetails"
    doc.setFontSize(10);
    doc.setFont("Helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text("Kursdetails", COURSE_DETAILS_LABEL_X, currentY);
    currentY += 2;

    // Obere Trennlinie
    doc.setDrawColor(COLOR_COURSE_DETAILS_LINE);
    doc.setLineWidth(0.3);
    doc.line(COURSE_DETAILS_LABEL_X, currentY, PAGE_WIDTH - PAGE_PADDING, currentY);
    currentY += COURSE_DETAILS_LINE_HEIGHT;

    // Key-Value-Paare rendern
    doc.setFontSize(9);

    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];

        // Seitenumbruch pruefen
        if (currentY > PAGE_BOTTOM_MARGIN) {
            doc.addPage();
            currentY = PAGE_PADDING;
        }

        // Label (grau, normal)
        doc.setFont("Helvetica", "normal");
        doc.setTextColor(COLOR_GREY_TEXT);
        doc.text(row.label, COURSE_DETAILS_LABEL_X, currentY);

        // Value (schwarz, normal) - mehrzeilige Werte unterstuetzen
        doc.setTextColor(0, 0, 0);
        const valueLines = stripNonPrintableForPdf(row.value).split("\n");

        for (let j = 0; j < valueLines.length; j++) {
            if (j > 0) {
                currentY += COURSE_DETAILS_LINE_HEIGHT;

                if (currentY > PAGE_BOTTOM_MARGIN) {
                    doc.addPage();
                    currentY = PAGE_PADDING;
                }
            }

            doc.text(valueLines[j], COURSE_DETAILS_VALUE_X, currentY);
        }

        currentY += COURSE_DETAILS_LINE_HEIGHT;
    }

    // Untere Trennlinie
    doc.setDrawColor(COLOR_COURSE_DETAILS_LINE);
    doc.setLineWidth(0.3);
    doc.line(COURSE_DETAILS_LABEL_X, currentY, PAGE_WIDTH - PAGE_PADDING, currentY);
    currentY += 4; // Abstand nach dem Block

    // Farben zuruecksetzen
    doc.setTextColor(0, 0, 0);

    return currentY;
}

/**
 * Prueft ob mindestens ein Fusszeilen-Feld vorhanden ist.
 * Wird verwendet um die Fusszeile nur bei vorhandenen Daten zu rendern.
 */
function hasFooterData(data: LetterPdfData): boolean {
    return !!(data.senderBankName || data.senderIban || data.senderBic || data.senderPhone || data.senderEmail || data.senderWebsite || data.senderTaxId || data.senderTaxNumber);
}

/**
 * Rendert die dreispaltige Geschaeftsfusszeile am unteren Seitenrand (DIN 5008).
 * Layout entspricht der Rechnungsfusszeile: Labels immer sichtbar, vertikale Trennlinien.
 * Spalte 1: Bank/IBAN/BIC | Spalte 2: Tel./E-Mail/Web | Spalte 3: Ust.-ID bzw. UID/Steuer-Nr.
 *
 * @param doc - Das jsPDF-Dokument
 * @param data - Briefdaten mit den Fusszeilen-Feldern
 */
function renderPageFooter(doc: JsPDF, data: LetterPdfData): void {
    doc.setFontSize(FOOTER_FONT_SIZE);
    doc.setTextColor(FOOTER_TEXT_COLOR);
    doc.setFont("Helvetica", "normal");

    // Spalten-Positionen
    var col1X = PAGE_PADDING + 1; // 1mm Innenabstand
    var col2X = PAGE_PADDING + FOOTER_COL_WIDTH;
    var col3X = PAGE_PADDING + 2 * FOOTER_COL_WIDTH;

    // Vertikale Trennlinien zwischen den Spalten
    var footerTopY = FOOTER_LINE_Y;
    var footerBottomY = FOOTER_Y + 3 * FOOTER_LINE_HEIGHT;
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.2);
    doc.line(col2X - 1, footerTopY, col2X - 1, footerBottomY);
    doc.line(col3X - 1, footerTopY, col3X - 1, footerBottomY);

    // Spalte 1: Bankdaten (Labels immer sichtbar, wie Rechnung)
    var y = FOOTER_Y;
    doc.text("Bank/Institut: " + (data.senderBankName || ""), col1X, y);
    y += FOOTER_LINE_HEIGHT;
    doc.text("IBAN: " + (data.senderIban || ""), col1X, y);
    y += FOOTER_LINE_HEIGHT;
    doc.text("BIC: " + (data.senderBic || ""), col1X, y);

    // Spalte 2: Kontaktdaten (Labels immer sichtbar, wie Rechnung)
    y = FOOTER_Y;
    var col2TextX = col2X + 1; // 1mm Innenabstand nach Trennlinie
    doc.text("Tel.: " + (data.senderPhone || ""), col2TextX, y);
    y += FOOTER_LINE_HEIGHT;
    doc.text("E-Mail: " + (data.senderEmail || ""), col2TextX, y);
    y += FOOTER_LINE_HEIGHT;
    doc.text("Web: " + (data.senderWebsite || ""), col2TextX, y);

    // Spalte 3: Steuerdaten (Labels immer sichtbar, wie Rechnung)
    y = FOOTER_Y;
    var col3TextX = col3X + 1; // 1mm Innenabstand nach Trennlinie
    // Landesübliches Label: "UID" für Schweizer/österreichische Nummern —
    // Präfix (CHE-/ATU) vorrangig, sonst Länder-Fallback wie im Rechnungs-PDF (#514).
    doc.text(getTaxIdLabel(data.senderTaxId, data.senderCountry) + ": " + (data.senderTaxId || ""), col3TextX, y);
    y += FOOTER_LINE_HEIGHT;
    doc.text("Steuer-Nr.: " + (data.senderTaxNumber || ""), col3TextX, y);

    // Farben zuruecksetzen
    doc.setTextColor(0, 0, 0);
}

/**
 * Rendert einen einzelnen DIN-5008-Brief in ein bereits erstelltes JsPDF-Dokument.
 * Wird von generateLetterPdf (Einzelbrief) und generateBatchLetterPdf (Batch) verwendet.
 *
 * @param doc - Das jsPDF-Dokument, in das gerendert wird
 * @param data - Briefdaten (Absender, Empfaenger, Inhalt)
 * @param startPage - 1-basierter Seitenindex, ab dem der aktuelle Brief beginnt
 *                     (relevant fuer die Geschaeftsfusszeile im Batch-Modus)
 */
async function renderSingleLetterToDoc(doc: JsPDF, data: LetterPdfData, startPage: number): Promise<void> {
    // --- LOGO (optional, oben links) ---
    if (data.senderLogoBase64) {
        try {
            // Bildformat erkennen (data:image/png;base64,... oder data:image/jpeg;base64,...)
            let format = "PNG";
            if (data.senderLogoBase64.indexOf("image/jpeg") !== -1 || data.senderLogoBase64.indexOf("image/jpg") !== -1) {
                format = "JPEG";
            }

            // Seitenverhaeltnis des Logos ermitteln und Dimensionen berechnen
            const dims = await getImageDimensions(data.senderLogoBase64);
            const aspectRatio = dims.width / dims.height;

            let logoWidth = LOGO_MAX_WIDTH;
            let logoHeight = logoWidth / aspectRatio;

            // Falls die berechnete Hoehe das Maximum ueberschreitet,
            // stattdessen von der maximalen Hoehe ausgehen
            if (logoHeight > LOGO_MAX_HEIGHT) {
                logoHeight = LOGO_MAX_HEIGHT;
                logoWidth = logoHeight * aspectRatio;
            }

            doc.addImage(
                data.senderLogoBase64,
                format,
                LOGO_X, // Linksbuendig platzieren
                LOGO_Y,
                logoWidth,
                logoHeight,
            );
        } catch (e) {
            // Logo-Fehler ignorieren - Brief wird ohne Logo generiert
        }
    }

    // --- ABSENDER-ZEILE (klein, grau, DIN 5008) ---
    doc.setFontSize(7);
    doc.setTextColor(COLOR_GREY_TEXT);
    doc.setFont("Helvetica", "normal");

    const senderParts: string[] = [];
    if (data.senderName) senderParts.push(data.senderName);

    const senderStreet = [data.senderStreet, data.senderStreetNumber].filter(Boolean).join(" ");
    if (senderStreet) senderParts.push(senderStreet);

    const senderCity = [data.senderZip, data.senderCity].filter(Boolean).join(" ");
    if (senderCity) senderParts.push(senderCity);

    const senderLine = senderParts.join(" \u2022 "); // Unicode Bullet (•)
    doc.text(senderLine, DIN_5008_ADDRESS_LEFT, SENDER_LINE_Y);

    // --- EMPFAENGER-BLOCK ---
    doc.setTextColor(0, 0, 0); // Zurueck zu schwarz
    let recipientY = RECIPIENT_START_Y;

    // Name (Vorname Nachname) - bold, 11pt
    const recipientName = [data.recipientFirstName, data.recipientLastName].filter(Boolean).join(" ");
    if (recipientName) {
        doc.setFontSize(11);
        doc.setFont("Helvetica", "bold");
        doc.text(recipientName, DIN_5008_ADDRESS_LEFT, recipientY);
        recipientY += 5.5;
    }

    doc.setFontSize(10);
    doc.setFont("Helvetica", "normal");

    // Adresszusatz (optional)
    if (data.recipientAddition) {
        doc.text(data.recipientAddition, DIN_5008_ADDRESS_LEFT, recipientY);
        recipientY += 5;
    }

    // Strasse + Hausnummer (Dedup, falls die Hausnummer bereits in der Strasse steht)
    const recipientStreet = formatStreetWithHouseNumber(data.recipientStreet, data.recipientHouseNumber);
    if (recipientStreet) {
        doc.text(recipientStreet, DIN_5008_ADDRESS_LEFT, recipientY);
        recipientY += 5;
    }

    // PLZ Stadt
    const recipientCity = [data.recipientPostCode, data.recipientCity].filter(Boolean).join(" ");
    if (recipientCity) {
        doc.text(recipientCity, DIN_5008_ADDRESS_LEFT, recipientY);
    }

    // --- DATUM (rechtsbuendig) ---
    doc.setFontSize(10);
    doc.setFont("Helvetica", "normal");
    const dateText = data.date || moment().format("DD.MM.YYYY");
    const dateX = PAGE_WIDTH - PAGE_PADDING; // 190mm
    doc.text(dateText, dateX, DATE_Y, {align: "right"});

    // --- BETREFF (bold, 11pt) ---
    doc.setFontSize(11);
    doc.setFont("Helvetica", "bold");
    doc.text(stripNonPrintableForPdf(data.subject), DIN_5008_ADDRESS_LEFT, SUBJECT_Y);

    let currentY = BODY_START_Y;

    // --- TEXTKÖRPER (headerText) ---
    doc.setTextColor(0, 0, 0);

    if (data.headerText) {
        currentY = renderHtmlTextBlock(doc, data.headerText, currentY, 10);
    }

    // --- FOOTER-TEXT ---
    if (data.footerText) {
        currentY += 4; // Abstand zwischen headerText und footerText

        // Seitenumbruch pruefen
        if (currentY > PAGE_BOTTOM_MARGIN) {
            doc.addPage();
            currentY = PAGE_PADDING;
        }

        currentY = renderHtmlTextBlock(doc, data.footerText, currentY, 10);
    }

    // --- SIGNATUR ---
    if (data.signature) {
        currentY += 6; // Groesserer Abstand vor der Signatur

        // Seitenumbruch pruefen
        if (currentY > PAGE_BOTTOM_MARGIN) {
            doc.addPage();
            currentY = PAGE_PADDING;
        }

        currentY = renderHtmlTextBlock(doc, data.signature, currentY, 10);
    }

    // --- GESCHAEFTSFUSSZEILE auf jeder Seite dieses Briefes rendern ---
    // startPage stellt sicher, dass im Batch-Modus nur die Seiten des
    // aktuellen Briefes mit der Fusszeile versehen werden (nicht vorherige Briefe).
    var totalPages = doc.getNumberOfPages();
    for (var pageIdx = startPage; pageIdx <= totalPages; pageIdx++) {
        doc.setPage(pageIdx);
        renderPageFooter(doc, data);
    }
}

/**
 * Verarbeitet das fertige JsPDF-Dokument je nach Use Case:
 * - "export": Startet Browser-Download
 * - "preview": Gibt Data-URI-String zurueck
 * - "print": Oeffnet Druckdialog via verstecktem iframe
 */
function handlePdfUseCase(doc: JsPDF, useCase: LetterPdfUseCase, fileName?: string): string | void {
    switch (useCase) {
        case "export":
            const exportBlob = doc.output("blob");
            const exportUrl = URL.createObjectURL(exportBlob);
            const downloadLink = document.createElement("a");

            downloadLink.href = exportUrl;
            downloadLink.download = (fileName || "Brief") + ".pdf";
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            // Verzoegertes Revoke damit der Browser den Download sicher starten kann
            setTimeout(() => {
                URL.revokeObjectURL(exportUrl);
            }, 150);
            return;

        case "preview":
            return doc.output("datauristring");

        case "print":
            printPdf(doc);
            return;

        default:
            return doc.output("datauristring");
    }
}

/**
 * Generiert ein DIN-5008-konformes Brief-PDF.
 *
 * @param data - Briefdaten (Absender, Empfaenger, Inhalt)
 * @param useCase - "preview" gibt Data-URI-String zurueck, "export" startet Download, "print" oeffnet Druckdialog
 * @param fileName - Dateiname fuer den Export (ohne .pdf-Endung)
 * @returns Data-URI-String bei "preview", void bei "export" und "print"
 */
export async function generateLetterPdf(data: LetterPdfData, useCase: LetterPdfUseCase, fileName?: string): Promise<string | void> {
    const doc = new JsPDF({
        orientation: "portrait",
    });

    doc.setLineHeightFactor(1);

    await renderSingleLetterToDoc(doc, data, 1);

    return handlePdfUseCase(doc, useCase, fileName);
}

/**
 * Generiert ein zusammengefuegtes PDF mit mehreren DIN-5008-Briefen.
 * Jeder Brief beginnt auf einer neuen Seite. Fusszeilen werden pro Brief
 * nur auf dessen eigene Seiten gerendert.
 *
 * @param letters - Array von Briefdaten fuer jeden einzelnen Brief
 * @param useCase - "preview" gibt Data-URI-String zurueck, "export" startet Download, "print" oeffnet Druckdialog
 * @param fileName - Dateiname fuer den Export (ohne .pdf-Endung)
 * @returns Data-URI-String bei "preview", void bei "export" und "print"
 */
export async function generateBatchLetterPdf(letters: LetterPdfData[], useCase: LetterPdfUseCase, fileName?: string): Promise<string | void> {
    if (letters.length === 0) return;

    const doc = new JsPDF({
        orientation: "portrait",
    });

    doc.setLineHeightFactor(1);

    for (var i = 0; i < letters.length; i++) {
        if (i > 0) {
            doc.addPage();
        }
        var startPage = doc.getNumberOfPages();
        await renderSingleLetterToDoc(doc, letters[i], startPage);
    }

    return handlePdfUseCase(doc, useCase, fileName);
}
